import { useState, useEffect } from 'react';
import { Badge } from '~/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Input } from '~/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';

type RowData = Record<string, string>;

interface ParseResult {
  headers: string[];
  rows: RowData[];
}

function parseTsv(tsvContent: string): ParseResult {
  const lines = tsvContent.trim().split('\n');
  if (lines.length === 0) return { headers: [], rows: [] };

  // Get all headers (including empty ones for alignment)
  const allHeaders = lines[0].split('\t').map(h => h.trim());
  // Filter to only non-empty headers for display
  const headers = allHeaders.filter(h => h);

  const rows: RowData[] = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const values = lines[i].split('\t').map(v => {
      // Remove quotes if present
      let val = v.trim();
      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1).replace(/""/g, '"');
      }
      return val;
    });

    // Pad values to match allHeaders length
    while (values.length < allHeaders.length) {
      values.push('');
    }

    const row: RowData = {};
    // Map values to headers, skipping empty header columns
    allHeaders.forEach((header, idx) => {
      if (header) {
        row[header] = values[idx] || '';
      }
    });
    rows.push(row);
  }

  return { headers, rows };
}

interface ImportPreviewTableProps {
  tsvContent: string;
  title: string;
  familyIds?: string[];
  onDataChange?: (data: RowData[]) => void;
  isChildrenTable?: boolean;
}

function ImportPreviewTable({
  tsvContent,
  title,
  familyIds = [],
  onDataChange,
  isChildrenTable = false,
}: ImportPreviewTableProps) {
  const { headers, rows } = parseTsv(tsvContent);
  const [editedData, setEditedData] = useState<RowData[]>(rows);

  // Update edited data when TSV content changes
  useEffect(() => {
    const { rows: newRows } = parseTsv(tsvContent);
    setEditedData(newRows);
  }, [tsvContent]);

  const handleCellChange = (rowIndex: number, column: string, value: string) => {
    const newData = [...editedData];
    newData[rowIndex] = { ...newData[rowIndex], [column]: value };
    setEditedData(newData);
    onDataChange?.(newData);
  };

  if (headers.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 text-center">
        No data to preview. Paste or upload TSV content.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{title}</h3>
        <Badge variant="secondary">{editedData.length} rows</Badge>
      </div>
      <div className="border rounded-md overflow-x-auto max-h-[400px] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              {headers.map((header) => (
                <TableHead key={header} className="min-w-[120px]">
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {editedData.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell className="text-xs text-muted-foreground">
                  {rowIndex + 1}
                </TableCell>
                {headers.map((header) => {
                  const value = row[header] || '';
                  const isFamilyIdColumn = isChildrenTable && header === 'Family ID';
                  const isUnmatchedFamilyId = isFamilyIdColumn && value && !familyIds.includes(value);

                  return (
                    <TableCell key={header} className="p-1">
                      {isFamilyIdColumn ? (
                        <div className="space-y-1">
                          <Select
                            value={value}
                            onValueChange={(newValue) => handleCellChange(rowIndex, header, newValue)}
                          >
                            <SelectTrigger className={`h-8 text-xs ${isUnmatchedFamilyId ? 'border-yellow-500' : ''}`}>
                              <SelectValue placeholder="Select Family ID" />
                            </SelectTrigger>
                            <SelectContent>
                              {familyIds.length === 0 ? (
                                <SelectItem value={value || ''} disabled>
                                  No families found
                                </SelectItem>
                              ) : (
                                familyIds.map((familyId) => (
                                  <SelectItem key={familyId} value={familyId}>
                                    {familyId}
                                  </SelectItem>
                                ))
                              )}
                              {value && !familyIds.includes(value) && (
                                <SelectItem value={value}>
                                  {value} (not found)
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {isUnmatchedFamilyId && (
                            <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-500">
                              Family ID not found in import
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <Input
                          value={value}
                          onChange={(e) => handleCellChange(rowIndex, header, e.target.value)}
                          className="h-8 text-xs"
                          placeholder="—"
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Export a function to get edited TSV from the component
export function getEditedTsvFromData(headers: string[], data: RowData[]) {
  if (data.length === 0) return '';

  const headerLine = headers.join('\t');
  const dataLines = data.map(row =>
    headers.map(header => {
      const value = row[header] || '';
      // Escape TSV: wrap in quotes if contains tab, newline, or quote
      if (value.includes('\t') || value.includes('\n') || value.includes('"')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join('\t')
  );

  return [headerLine, ...dataLines].join('\n');
}

export default ImportPreviewTable;
